import { Button, Spinner, Textarea } from '@crm/ui';
import { ImagePlus, Send, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { reduzirImagem } from '@/components';

interface MessageComposerProps {
  onSend: (input: { body: string; images: string[] }) => void;
  enviando: boolean;
  placeholder?: string;
}

/**
 * O que se escreve num chamado: texto e imagens. A imagem entra pelo botao ou
 * colada direto da area de transferencia - quem tira print cola, nao salva
 * arquivo antes.
 */
export function MessageComposer({ onSend, enviando, placeholder }: MessageComposerProps) {
  const [body, setBody] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  async function adicionar(arquivos: File[]) {
    const novas = await Promise.all(
      arquivos.filter((arquivo) => arquivo.type.startsWith('image/')).map(reduzirImagem),
    );

    setImages((atual) => [...atual, ...novas].slice(0, 5));
  }

  function enviar() {
    if (!body.trim() && images.length === 0) return;

    onSend({ body: body.trim(), images });
    setBody('');
    setImages([]);
  }

  return (
    <div className="space-y-3">
      <Textarea
        rows={3}
        value={body}
        placeholder={placeholder ?? 'Escreva sua mensagem'}
        onChange={(event) => setBody(event.target.value)}
        onPaste={(event) => {
          const arquivos = [...event.clipboardData.files];
          if (arquivos.length > 0) void adicionar(arquivos);
        }}
      />

      {images.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {images.map((image) => (
            <li key={image} className="relative">
              <img
                src={image}
                alt=""
                className="h-20 w-20 rounded-card border border-border object-cover"
              />
              <button
                type="button"
                aria-label="Remover imagem"
                onClick={() => setImages((atual) => atual.filter((item) => item !== image))}
                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(event) => {
            void adicionar([...(event.target.files ?? [])]);
            event.target.value = '';
          }}
        />

        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
          <ImagePlus className="h-4 w-4" />
          Anexar imagem
        </Button>

        <Button type="button" disabled={enviando} onClick={enviar}>
          {enviando ? <Spinner /> : <Send className="h-4 w-4" />}
          Enviar
        </Button>

        <span className="text-xs text-muted-foreground">Ctrl+V cola a imagem copiada.</span>
      </div>
    </div>
  );
}
